import FuseAnimate from '@fuse/core/FuseAnimate';
import Fab from '@mui/material/Fab';
import Icon from '@mui/material/Icon';
import { makeStyles } from '@mui/styles';
import withReducer from 'app/store/withReducer';
import clsx from 'clsx';
import moment from 'moment';
import React, { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import * as ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import OAuthService from 'src/app/auth/services/oAuthService';
import oAuthServiceConfig from 'src/app/auth/services/oAuthService/oAuthServiceConfig';
import CalendarHeader from '../calendar/CalendarHeader';
import * as Actions from '../design-studio/store/actions'
import reducer from '../design-studio/store/reducers';
import * as Flags from 'country-flag-icons/react/3x2';
import '../calendar/Calendar.css';
import { Alert, AlertTitle, Button, Select, Typography } from '@mui/material';
import { color } from '@mui/system';
import customStyles from 'app/shared-components/SelectCustomStyle';
import CalendarMeeting from './CalendarMeeting';
import { useNavigate } from 'react-router-dom';
import MeetingDialog from './MeetingDialog';
import Steps from './Steps';
import ListPageHeader from '../utilities/ListPageHeader';

moment.locale('ko', {
	week: {
		dow: 1,
		doy: 1,
	},
});

const localizer = momentLocalizer(moment);

const DragAndDropCalendar = withDragAndDrop(Calendar);

const allViews = Object.keys(Views).map(k => Views[k]);

const useStyles = makeStyles(theme => ({
	root: {
		borderRight: `1px solid rgba(241,245,249,.12)`,

		'& .rbc-header': {
			padding: '12px 6px',
			fontWeight: 600,
			fontSize: 14
		},
		'& .rbc-label': {
			padding: '8px 6px'
		},
		'& .rbc-today': {
			backgroundColor: 'transparent'
		},
		'& .rbc-header.rbc-today, & .rbc-month-view .rbc-day-bg.rbc-today': {
			borderBottom: `2px solid ${theme.palette.secondary.main}!important`,
			background: `${theme.palette.secondary.main}`,
		},
		'& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view': {
			padding: 24,
			[theme.breakpoints.down('sm')]: {
				padding: 16
			},
			...theme.mixins.border(0)
		},
		'& .rbc-agenda-view table': {
			...theme.mixins.border(1),
			'& thead > tr > th': {
				...theme.mixins.borderBottom(0)
			},
			'& tbody > tr > td': {
				padding: '12px 6px',
				'& + td': {
					...theme.mixins.borderLeft(1)
				}
			}
		},
		'& .rbc-time-view': {
			'& .rbc-time-header': {
				...theme.mixins.border(1)
			},
			'& .rbc-time-content': {
				flex: '0 1 auto',
				...theme.mixins.border(1)
			}
		},
		'& .rbc-month-view': {
			'& > .rbc-row': {
				...theme.mixins.border(1)
			},
			'& .rbc-month-row': {
				...theme.mixins.border(1),
				borderWidth: '0 1px 1px 1px!important',
				minHeight: 90
			},
			'& .rbc-header + .rbc-header': {
				...theme.mixins.borderLeft(1)
			},
			'& .rbc-header': {
				...theme.mixins.borderBottom(0)
			},
			'& .rbc-day-bg + .rbc-day-bg': {
				...theme.mixins.borderLeft(1)
			},
			'& .saturday': {
				background: `#2b3138`,
			},
			'& .sunday': {
				background: `#2b3138`,
			}
		},
		'& .rbc-day-slot .rbc-time-slot': {
			...theme.mixins.borderTop(1),
			opacity: 0.5
		},
		'& .rbc-time-header > .rbc-row > * + *': {
			...theme.mixins.borderLeft(1)
		},
		'& .rbc-time-content > * + * > *': {
			...theme.mixins.borderLeft(1)
		},
		'& .rbc-day-bg + .rbc-day-bg': {
			...theme.mixins.borderLeft(1)
		},
		'& .rbc-time-header > .rbc-row:first-child': {
			...theme.mixins.borderBottom(1)
		},
		'& .rbc-timeslot-group': {
			minHeight: 64,
			...theme.mixins.borderBottom(1)
		},
		'& .rbc-date-cell': {
			padding: 8,
			fontSize: 16,
			fontWeight: 400,
			opacity: 0.5,
			'& > a': {
				color: 'inherit'
			}
		},
		'& .rbc-event': {
			borderRadius: 4,
			padding: '4px 8px',
			backgroundColor: theme.palette.primary.dark,
			color: theme.palette.primary.contrastText,
			boxShadow: theme.shadows[0],
			transitionProperty: 'box-shadow',
			transitionDuration: theme.transitions.duration.short,
			transitionTimingFunction: theme.transitions.easing.easeInOut,
			position: 'relative',
			'&:hover': {
				boxShadow: theme.shadows[2]
			}
		},
		'& .rbc-row-segment': {
			padding: '0 4px 4px 4px'
		},
		'& .rbc-off-range-bg': {
			backgroundColor: theme.palette.type === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.16)'
		},
		'& .rbc-show-more': {
			color: theme.palette.secondary.main,
			background: 'transparent'
		},
		'& .rbc-addons-dnd .rbc-addons-dnd-resizable-month-event': {
			position: 'static'
		},
		'& .rbc-addons-dnd .rbc-addons-dnd-resizable-month-event .rbc-addons-dnd-resize-month-event-anchor:first-child': {
			left: 0,
			top: 0,
			bottom: 0,
			height: 'auto'
		},
		'& .rbc-addons-dnd .rbc-addons-dnd-resizable-month-event .rbc-addons-dnd-resize-month-event-anchor:last-child': {
			right: 0,
			top: 0,
			bottom: 0,
			height: 'auto'
		}
	},
	addButton: {
		position: 'absolute',
		right: 55,
		top: 115,
		// zIndex: 99,
		width: 150
	}
}));


function DsCalendarMain(props) {
	const navigate = useNavigate();
	const [events, setEvents] = useState([]);
	const [error, setError] = useState();
	const [showPaginationDropdown, setShowPaginationDropdown] = useState(false);
	const dispatch = useDispatch();
	const [date, setDate] = useState(new Date())
	const classes = useStyles(props);
	const headerEl = useRef(null);
	const [selectedYear, setSelectedYear] = useState({
		value: new Date().getFullYear()
	});
	const [years, setYears] = useState('')

	useEffect(() => {
		let currentYears = new Date().getFullYear() + 1
		let allYears = [];
		for (let i = 0; i < 10; i++) {
			allYears.push({
				label: currentYears,
				value: currentYears
			})
			currentYears -= 1;
		}
		// console.log('currentYears', currentYears)
		setYears(allYears)
		changeYear(allYears[1])
	}, [])

	useEffect(() => {
		loadData()
	}, [])

	const changeYear = (data) => {
		const m = moment(date).month();
		setDate(new Date(data.value, m))
		setSelectedYear(data);
	};

	const loadData = async () => {
		try {
			const startDate = moment().startOf('month').format('yyyy-MM-DD')
			const endDate = moment().add(1, 'months').startOf('month').format('yyyy-MM-DD')
			// const res = await OAuthService.getList(`${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.dsFinddEvents}?scheduledStartDate='2023-08-01&scheduledEndDate=2023-09-02`);
			const res = await OAuthService.getList(`${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.dsFinddEvents}?scheduledStartDate=${startDate}&scheduledEndDate=${endDate}`);
			const tasks = res?.data?.map(obj => ({
				id: obj?.dsTaskId,
				allDay: false,
				title: obj?.description,
				start: new Date(obj?.scheduledStartDate),
				end: new Date(obj?.scheduledEndDate),
				desc: obj?.location
			}))
			setEvents(tasks)

		} catch (error) {
			setError(error.message);
		}
	}


	useEffect(() => {
		loadData()
	}, []);

	const handleBack = () => {
		localStorage.setItem('backAction', true)
		navigate('/new-request')
	}

	const dayPropGetter = useCallback((date) => ({
		...(moment(date).day() === 6 && {
			className: 'saturday',
		}),
		...(moment(date).day() === 0 && {
			className: 'sunday',
		}),
	}),
		[]
	)

	return (
		<>
			{error && <Alert severity="error" className="mx-16 mt-16">
				<AlertTitle>Error</AlertTitle>
				{error}
			</Alert>}
			{/* <Steps className='mt-20' /> */}
			<ListPageHeader
				componentName="Date & Time slot selection"
				pageName={`Date & Time slot selection`}
				handleBack={handleBack}
				showPaginationDropdown={showPaginationDropdown}
			/>
			<div className='flex flex-row'>
				<div className={clsx(classes.root, 'flex flex-col flex-auto relative')}>

					<div ref={headerEl} />
					<CalendarMeeting selectedYear={selectedYear} changeYear={changeYear} />
					<DragAndDropCalendar
						className="flex flex-1 container"
						selectable
						localizer={localizer}
						events={events && events}
						resizable
						defaultView={Views.MONTH}
						// defaultDate={new Date()}
						date={date}
						startAccessor="start"
						endAccessor="end"
						// views={allViews}
						views={{
							day: true,
							week: true,
							month: true,
						}}
						step={60}
						showMultiDayTimes
						onNavigate={date => setDate(date)}
						dayPropGetter={dayPropGetter}
						components={{
							toolbar: _props => {
								return headerEl.current
									? ReactDOM.createPortal(<CalendarHeader changeYear={changeYear} {..._props} />, headerEl.current)
									: null;
							}
						}}
						// onNavigate={handleNavigate}
						onSelectEvent={event => {
							dispatch(Actions.openEditEventDialog(event));
						}}
						onSelectSlot={slotInfo => {
							if (moment(slotInfo.start).isBefore(moment(), 'day')) {
								return;
							}
							const end = slotInfo.end;
							end.setDate(slotInfo.end.getDate() - 1)
							dispatch(
								Actions.openNewEventDialog({
									start: slotInfo.start,
									end: end
								})
							)
						}
						}
					/>
					{/* <FuseAnimate animation="transition.expandIn" delay={500}>
						<Fab
							color="primary"
							variant="outlined"
							aria-label="add"
							className={classes.addButton}
						// onClick={() =>
						// 	dispatch(
						// 		Actions.openNewEventDialog({
						// 			start: new Date(),
						// 			end: new Date(),
						// 		})
						// 	)
						// }
						>
							<Icon>add</Icon>
						</Fab>
					</FuseAnimate> */}
					{/* <Button variant='contained' color='warning' className={classes.addButton} onClick={() => navigate('/thank-you')}>Finish</Button> */}
					<MeetingDialog />
					{/* <EventDialog getCalendarEvent={() => searchData()} selectedCountry={selectedCountry} countryOptions={countryOptions} /> */}
				</div>
				{/* <HolidaysList holiday={events} loadData={searchData} /> */}
			</div>
		</>
	)
}

export default withReducer('dSCalendarApp', reducer)(DsCalendarMain);